import { _bytesToBigInt, _bigIntToBytes, _modPow, _createRandomInRange, _modInverse, _getBigInt } from '../../../utils';
var _PdfRsaCoreAlgorithm = /** @class */ (function () {
    function _PdfRsaCoreAlgorithm() {
    }
    _PdfRsaCoreAlgorithm.prototype._getInputBlockSize = function () {
        return this._isEncryption ? (this._bitSize - 1) >>> 3 : (this._bitSize + 7) >>> 3;
    };
    _PdfRsaCoreAlgorithm.prototype._getOutputBlockSize = function () {
        return this._isEncryption ? (this._bitSize + 7) >>> 3 : (this._bitSize - 1) >>> 3;
    };
    _PdfRsaCoreAlgorithm.prototype._initialize = function (isEncryption, parameters) {
        this._key = parameters;
        this._isEncryption = isEncryption;
        this._bitSize = this._key.modulus._bitLength();
    };
    _PdfRsaCoreAlgorithm.prototype._convertInput = function (bytes, offset, length) {
        var subBytes = bytes.subarray(offset, offset + length);
        if (subBytes.length > this._getInputBlockSize() + 1) {
            throw new Error('Input data too large for RSA block.');
        }
        var input = _bytesToBigInt(subBytes);
        if (input >= this._key.modulus) {
            throw new Error('Input data is larger than modulus.');
        }
        return input;
    };
    _PdfRsaCoreAlgorithm.prototype._convertOutput = function (result) {
        var output = _bigIntToBytes(result);
        if (this._isEncryption) {
            var outSize = this._getOutputBlockSize();
            if (output.length < outSize) {
                var paddedOutput = new Uint8Array(outSize);
                paddedOutput.set(output, outSize - output.length);
                return paddedOutput;
            }
        }
        return output;
    };
    _PdfRsaCoreAlgorithm.prototype._processBlock = function (input) {
        var bigInt = _getBigInt();
        var privateKey = this._key; // eslint-disable-line
        if (privateKey._isPrivate && privateKey.p && privateKey.q) {
            var p = privateKey.p._toBigInt();
            var q = privateKey.q._toBigInt();
            var dP = privateKey.dP._toBigInt();
            var dQ = privateKey.dQ._toBigInt();
            var qInv = privateKey.inverse._toBigInt();
            var mP = _modPow(input % p, dP, p);
            var mQ = _modPow(input % q, dQ, q);
            var h = (mP - mQ) * qInv;
            h = h % p;
            if (h < bigInt('0')) {
                h += p;
            }
            var m = h * q;
            m = m + mQ;
            return m;
        }
        return _modPow(input, this._key.exponent, this._key.modulus);
    };
    return _PdfRsaCoreAlgorithm;
}());
export { _PdfRsaCoreAlgorithm };
var _PdfRsaAlgorithm = /** @class */ (function () {
    function _PdfRsaAlgorithm() {
        this._rsaCoreEngine = new _PdfRsaCoreAlgorithm();
    }
    _PdfRsaAlgorithm.prototype._getAlgorithmName = function () {
        return 'RSA';
    };
    _PdfRsaAlgorithm.prototype._getInputBlock = function () {
        return this._rsaCoreEngine._getInputBlockSize();
    };
    _PdfRsaAlgorithm.prototype._getOutputBlock = function () {
        return this._rsaCoreEngine._getOutputBlockSize();
    };
    _PdfRsaAlgorithm.prototype._initialize = function (isEncryption, parameter) {
        this._rsaCoreEngine._initialize(isEncryption, parameter);
        this._key = parameter;
    };
    _PdfRsaAlgorithm.prototype._processBlock = function (bytes, offset, length) {
        var bigInt = _getBigInt();
        if (!this._key) {
            throw new Error('RSA engine not initialized.');
        }
        var input = this._rsaCoreEngine._convertInput(bytes, offset, length);
        var result;
        var privateKey = this._key; // eslint-disable-line
        if (privateKey._isPrivate && privateKey.publicExponent) {
            var e = privateKey.publicExponent._toBigInt();
            var m = this._key.modulus._toBigInt();
            var r = _createRandomInRange(bigInt(1), m - bigInt(1));
            var blindedInput = (_modPow(r, e, m) * input) % m;
            var blindedResult = this._rsaCoreEngine._processBlock(blindedInput);
            var rInverse = _modInverse(r, m);
            result = (blindedResult * rInverse) % m;
        }
        else {
            result = this._rsaCoreEngine._processBlock(input);
        }
        return this._rsaCoreEngine._convertOutput(result);
    };
    return _PdfRsaAlgorithm;
}());
export { _PdfRsaAlgorithm };
